/**
 * Summary counts and per-email status table after Create Administrators submit.
 */

import { observer } from 'mobx-react-lite';

import Card from '../../../../../components/layouts/card/card';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import FontHeading4 from '../../../../../components/fonts/font-heading4/font-heading4';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import Table from '../../../../../components/table/table';
import TableRow from '../../../../../components/table/components/table-row/table-row';
import { ICreateAdministratorsResult } from '../../../../../services/api/troubleshooting-service';
import createAdministratorsStore from '../stores/create-administrators-store';

const RESULTS_COLUMNS = [
    { content: 'Email', width: { value: 1, type: 'fr' as const } },
    { content: 'Status', width: { value: 1, type: 'fr' as const } },
];

function summarizeCreateAdministratorsResult(result: ICreateAdministratorsResult): string {
    if (!result.user_results?.length) {
        return `Created: ${result.created.length}, Skipped: ${result.skipped.length}, Failed: ${result.failed.length}`;
    }
    const messageCounts = new Map<string, number>();
    for (const resultRow of result.user_results) {
        const nextCount = (messageCounts.get(resultRow.message) ?? 0) + 1;
        messageCounts.set(resultRow.message, nextCount);
    }
    const preferredOrder = [
        'Created',
        'roles changed',
        'Store user promoted; roles changed',
        'Already created with the right roles',
        'Store user promoted; already created with the right roles',
        'Failed',
        'User exists; update manually',
    ];
    const summaryParts: string[] = [];
    for (const messageLabel of preferredOrder) {
        const countForMessage = messageCounts.get(messageLabel);
        if (countForMessage) {
            summaryParts.push(`${messageLabel}: ${countForMessage}`);
        }
    }
    const preferredOrderSet = new Set<string>(preferredOrder);
    for (const [messageLabel, countForMessage] of messageCounts) {
        if (!preferredOrderSet.has(messageLabel)) {
            summaryParts.push(`${messageLabel}: ${countForMessage}`);
        }
    }
    return summaryParts.join(', ');
}

const CreateAdministratorsResultsCard: React.FC = observer(() => {
    const store = createAdministratorsStore;

    if (!store.result) {
        return null;
    }

    const { created, skipped, failed, user_results: userResults } = store.result;

    return (
        <div className="create-administrators-page__results-card">
            <Card isExpandable={false}>
                <FontHeading4>Results</FontHeading4>
                <FontBody2 color={FONT_COLOR.PRIMARY1} variant={[FONT_VARIANT.BOLD]}>
                    {summarizeCreateAdministratorsResult(store.result)}
                </FontBody2>
                <div className="create-administrators-page__table-wrapper">
                    <Table columns={RESULTS_COLUMNS}>
                        {userResults && userResults.length > 0
                            ? userResults.map((resultRow, rowIndex) => (
                                  <TableRow
                                      key={`result-${resultRow.email}-${rowIndex}`}
                                      name="user-result"
                                      cells={[resultRow.email, resultRow.message]}
                                  />
                              ))
                            : [
                                  ...created.map((administratorEmail, rowIndex) => (
                                      <TableRow
                                          key={`created-${administratorEmail}-${rowIndex}`}
                                          name="created"
                                          cells={[administratorEmail, 'Created']}
                                      />
                                  )),
                                  ...skipped.map((administratorEmail, rowIndex) => (
                                      <TableRow
                                          key={`skipped-${administratorEmail}-${rowIndex}`}
                                          name="skipped"
                                          cells={[administratorEmail, 'Skipped (already exists)']}
                                      />
                                  )),
                                  ...failed.map((administratorEmail, rowIndex) => (
                                      <TableRow
                                          key={`failed-${administratorEmail}-${rowIndex}`}
                                          name="failed"
                                          cells={[administratorEmail, 'Failed']}
                                      />
                                  )),
                              ]}
                    </Table>
                </div>
            </Card>
        </div>
    );
});

export default CreateAdministratorsResultsCard;
